import { useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { PaymentForm } from '../../interfaces/Checkout/PaymentForm';
import { BasketSummary } from '../../interfaces/Basket/BasketSummary';
import { UserSummary } from '../../interfaces/User/UserSummary';
import { formatCurrency } from '../../utils/Currency';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type PointCardProps = {
  form: PaymentForm;
  subtotal: number;
  basketSummary: BasketSummary;
  userSummary: UserSummary;
  onUsePoint: (amount: number) => void;
};

export const PointCard = ({
  form,
  subtotal,
  basketSummary,
  userSummary,
  onUsePoint,
}: PointCardProps) => {
  const t = useLocalizedText();

  const { primary, secondary } = useThemeColor();

  const [value, setValue] = useState(0);

  let error = '';
  if (userSummary.point < basketSummary.minPointUsageValue) {
    error = t('checkout.point_card.insufficient_points', {
      minValue: basketSummary.minPointUsageValue,
    });
  } else if (subtotal < basketSummary.pointMinValue) {
    error = t('checkout.point_card.insufficient_basket_total', {
      minValue: formatCurrency(basketSummary.pointMinValue),
    });
  }

  const maxAllowedPoint = Math.min(
    userSummary.point,
    Math.floor(subtotal * basketSummary.pointValue),
  );

  const handleChange = (newValue: number) => {
    setValue(
      newValue
        ? Math.max(
            Math.min(maxAllowedPoint, newValue),
            basketSummary.minPointUsageValue,
          )
        : 0,
    );
  };

  return basketSummary.canUsePointSystem ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 1.5,
        alignItems: 'stretch',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          px: 2.5,
          pt: 1,
          pb: 2,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          {t('checkout.point_card.points', { points: userSummary.point })}
        </Typography>

        {error ? (
          <Typography>{error}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <NumberInput
                disabled={!!form.point}
                value={value}
                onChange={handleChange}
              />

              <Button
                disabled={!!form.point || !value}
                variant='contained'
                onClick={() => onUsePoint(value / basketSummary.pointValue)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: secondary,
                  backgroundColor: primary,
                }}
              >
                {t('common.confirm')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  ) : null;
};
